/**
 * Editing Plugin System
 *
 * Manages inline editing of canvas nodes via a plugin architecture.
 * Plugins handle the actual editing UI (contentEditable, Tiptap, etc.)
 * while the EditingManager orchestrates lifecycle and state.
 */

import type { InputSystem } from "@rool/input";
import type { DirectCanvas } from "@rool/renderer";
import { createEditingToolbar, type EditingToolbar, type ToolbarEntry } from "./toolbar";

// =============================================================================
// Plugin Interface
// =============================================================================

/**
 * Context provided to editing plugins when starting an edit session.
 */
export interface EditingPluginContext {
  /** ID of the node being edited */
  nodeId: string;
  /** The editable element inside the node (found via editableSelector) */
  element: HTMLElement;
  /** The .canvas-node container element */
  nodeElement: HTMLElement;
  /** The canvas container element (for toolbar positioning) */
  container: HTMLElement;
  /** DirectCanvas instance (for coordinate conversion, invalidation) */
  canvas: DirectCanvas;
  /** Input system (for pushMode/popMode) */
  input: InputSystem;
  /** Current content of the editable element */
  originalContent: string;
  /** Node size */
  nodeSize: { width: number; height: number };
  /** Node position */
  nodePosition: { x: number; y: number };
  /** Resize the node (center-based bounds) */
  resizeNode: (bounds: {
    cx: number;
    cy: number;
    width: number;
    height: number;
  }) => void;
  /** Trigger a canvas re-render */
  render: () => void;
  /** Get fresh node state (position, size, data) — reads from the live data store */
  getNodeState: () => {
    position: { x: number; y: number };
    size: { width: number; height: number };
    data: unknown;
  } | undefined;
  /**
   * Request that editing be stopped through the proper lifecycle chain
   * (EditingManager → CanvasEditor → ViewState). Plugins should call this
   * from keyboard shortcuts (Escape, Enter, Cmd+Enter) instead of calling
   * their own stop functions directly.
   */
  requestStop: (save: boolean) => void;
}

/**
 * An active editing session returned by a plugin.
 */
export interface EditingSession {
  /** Get current content from the editor */
  getContent: () => string;
  /** Stop the editing session. Returns the result (content + optional size). */
  stop: (save: boolean) => EditingResult;
  /** Called when the camera changes (to reposition toolbars etc.) */
  onCameraChange?: () => void;
  /** Current toolbar entries. Called initially and on each toolbar change signal. */
  getToolbar?: () => ToolbarEntry[];
  /** Subscribe to toolbar state changes. Returns unsubscribe function. */
  onToolbarChange?: (callback: () => void) => (() => void);
}

/**
 * Result of stopping an editing session.
 */
export interface EditingResult {
  /** Final content */
  content: string;
  /** Optional new size (for content that changed the node dimensions) */
  size?: { width: number; height: number };
}

/**
 * An editing plugin that can handle editing for specific node types.
 */
export interface EditingPlugin {
  /** Node types this plugin handles (e.g., ['markdown'], ['header', 'sticky-note']) */
  nodeTypes: string[];
  /**
   * CSS selector for the editable element within the node.
   * The manager uses this to find the element to pass to startEditing.
   */
  editableSelector: string;
  /** Start an editing session. Returns a session object for lifecycle control. */
  startEditing: (ctx: EditingPluginContext) => EditingSession;
}

// =============================================================================
// Editing Manager
// =============================================================================

export interface EditingManagerOptions {
  /** DirectCanvas instance */
  canvas: DirectCanvas;
  /** Canvas container element */
  container: HTMLElement;
  /** Input system */
  input: InputSystem;
  /** Get node data by ID */
  getNode: (
    nodeId: string,
  ) => {
    type: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    data: unknown;
  } | undefined;
  /** Resize a node (center-based bounds) */
  resizeNode: (
    nodeId: string,
    bounds: { cx: number; cy: number; width: number; height: number },
  ) => void;
  /** Trigger a canvas re-render */
  render: () => void;
  /**
   * Called before editing starts. Return false to prevent editing.
   * Use this to check if the node type supports editing.
   */
  onEditStart?: (nodeId: string, nodeType: string) => boolean | void;
  /**
   * Called when editing ends with the result.
   * Use this for SDK persistence.
   */
  onEditEnd?: (
    nodeId: string,
    content: string,
    size?: { width: number; height: number },
  ) => void;
  /**
   * Called when the node needs to be invalidated for re-rendering.
   * Default: calls canvas.invalidateNode.
   */
  invalidateNode?: (nodeId: string) => void;
  /**
   * Called when a plugin requests editing to stop (e.g., Escape, Cmd+Enter).
   * The CanvasEditor uses this to route through handleEditStop, which
   * clears ViewState in addition to stopping the plugin session.
   */
  onStopRequested?: (save: boolean) => void;
  /**
   * Called after a node is resized during an active editing session.
   * Use this to adjust the camera/viewport to keep the node visible.
   */
  onResizeDuringEdit?: (nodeId: string) => void;
}

/**
 * Manages editing sessions using registered plugins.
 */
export class EditingManager {
  private readonly plugins: EditingPlugin[] = [];
  private readonly options: EditingManagerOptions;
  private activeSession: EditingSession | null = null;
  private activeNodeId: string | null = null;
  private activeToolbar: EditingToolbar | null = null;
  private toolbarUnsubscribe: (() => void) | null = null;

  constructor(options: EditingManagerOptions) {
    this.options = options;
  }

  /** Register an editing plugin */
  registerPlugin(plugin: EditingPlugin): void {
    this.plugins.push(plugin);
  }

  /** Get the plugin for a given node type */
  private getPluginForType(nodeType: string): EditingPlugin | undefined {
    return this.plugins.find((p) => p.nodeTypes.includes(nodeType));
  }

  /** Get the currently editing node ID */
  getEditingNodeId(): string | null {
    return this.activeNodeId;
  }

  /** Check if currently editing */
  isEditing(): boolean {
    return this.activeSession !== null;
  }

  /**
   * Start editing a node. Finds the appropriate plugin, locates the
   * editable element, and starts a session.
   *
   * Returns true if editing was started.
   */
  startEditing(nodeId: string): boolean {
    // Already editing this node — no-op
    if (this.activeNodeId === nodeId && this.activeSession) return true;

    // Stop any current session first
    if (this.activeSession) {
      this.stopEditing(true);
    }

    const node = this.options.getNode(nodeId);
    if (!node) return false;

    const plugin = this.getPluginForType(node.type);
    if (!plugin) return false;

    // Check with app if editing should proceed
    if (this.options.onEditStart) {
      const result = this.options.onEditStart(nodeId, node.type);
      if (result === false) return false;
    }

    // Find the node element in DOM
    const nodeElement = this.options.canvas.getNodeElement(nodeId);
    if (!nodeElement) return false;

    // Check if texture-cached (can't edit texture-cached nodes)
    if (nodeElement.classList.contains("texture-cached")) return false;

    // Find the editable element
    const editableElement = nodeElement.querySelector(
      plugin.editableSelector,
    ) as HTMLElement;
    if (!editableElement) return false;

    // Extract original content
    const originalContent = this.extractContent(editableElement, node.type);

    // Create context for the plugin
    const ctx: EditingPluginContext = {
      nodeId,
      element: editableElement,
      nodeElement,
      container: this.options.container,
      canvas: this.options.canvas,
      input: this.options.input,
      originalContent,
      nodeSize: { ...node.size },
      nodePosition: { ...node.position },
      resizeNode: (bounds) => {
        this.options.resizeNode(nodeId, bounds);
        this.options.onResizeDuringEdit?.(nodeId);
      },
      render: () => this.options.render(),
      getNodeState: () => {
        const n = this.options.getNode(nodeId);
        if (!n) return undefined;
        return { position: n.position, size: n.size, data: n.data };
      },
      requestStop: (save: boolean) => {
        if (this.options.onStopRequested) {
          this.options.onStopRequested(save);
        } else {
          this.stopEditing(save);
        }
      },
    };

    // Start the session
    this.activeSession = plugin.startEditing(ctx);
    this.activeNodeId = nodeId;

    // Create toolbar if the session provides one
    if (this.activeSession.getToolbar) {
      const session = this.activeSession;
      this.activeToolbar = createEditingToolbar(
        session.getToolbar!(),
        nodeElement,
        this.options.container,
      );

      if (session.onToolbarChange) {
        this.toolbarUnsubscribe = session.onToolbarChange(() => {
          this.activeToolbar?.update(session.getToolbar!());
        });
      }
    }

    return true;
  }

  /**
   * Stop the current editing session.
   *
   * @param save - Whether to save the changes
   * @returns The editing result, or null if not editing
   */
  stopEditing(save: boolean): EditingResult | null {
    if (!this.activeSession || !this.activeNodeId) return null;

    const session = this.activeSession;
    const nodeId = this.activeNodeId;

    // Clean up toolbar
    this.toolbarUnsubscribe?.();
    this.toolbarUnsubscribe = null;
    this.activeToolbar?.destroy();
    this.activeToolbar = null;

    // Clear state first to prevent re-entrant calls
    this.activeSession = null;
    this.activeNodeId = null;

    // Stop the session
    const result = session.stop(save);

    // Invalidate the node for re-rendering
    const invalidate =
      this.options.invalidateNode ??
      ((id: string) => this.options.canvas.invalidateNode(id));
    invalidate(nodeId);

    // Notify app for persistence
    if (save && result.content !== undefined) {
      this.options.onEditEnd?.(nodeId, result.content, result.size);
    }

    return result;
  }

  /** Notify the active session of a camera change */
  onCameraChange(): void {
    this.activeSession?.onCameraChange?.();
    this.activeToolbar?.updatePosition();
  }

  /** Clean up */
  destroy(): void {
    if (this.activeSession) {
      this.stopEditing(false);
    }
  }

  /**
   * Extract content from an editable element.
   * For elements with placeholder class, returns empty string.
   */
  private extractContent(element: HTMLElement, _nodeType: string): string {
    if (element.classList.contains("header-empty")) return "";
    return element.innerText || "";
  }
}
